import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CLINICAL_TRIAL_ATTRIBUTES,
  VACCINE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Moderna 1273 COVID-19 Vaccine Study
 * Target population: Adults 65+ willing to receive a COVID-19 vaccine
 */
export const moderna1273ScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 65 and older.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    id: "willing_covid_vaccine",
    type: QuestionType.RADIO,
    question: "Are you willing to receive a COVID-19 vaccine as part of this study?",
    helpText: "This study involves receiving an investigational COVID-19 vaccine designed to target newer variants.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am willing to receive a COVID-19 vaccine"
      },
      {
        value: "no",
        label: "No, I am not willing to receive a COVID-19 vaccine",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study requires participants to be willing to receive a COVID-19 vaccine.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },

  {
    id: "recent_covid_infection",
    type: QuestionType.RADIO,
    question: "Have you had a confirmed COVID-19 infection in the past 6 months?",
    helpText: "This refers to a positive COVID-19 test (PCR or antigen) in the past 6 months.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had a confirmed COVID-19 infection in the past 6 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had a confirmed COVID-19 infection in the past 6 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a COVID-19 infection in the past 6 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 24406, booleanAttributeValue: true }, // had_covid_6_months
      ],
      no: [
        { attributeDefinitionId: 24406, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_covid_vaccine",
    type: QuestionType.RADIO,
    question: "Have you received any COVID-19 vaccine in the past 6 months?",
    helpText: "This includes any COVID-19 vaccine such as Moderna, Pfizer, or Novavax.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received a COVID-19 vaccine in the past 6 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received a COVID-19 vaccine in the past 6 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received a COVID-19 vaccine in the past 6 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: VACCINE_ATTRIBUTES.HAD_COVID_VACCINE_6_MONTHS, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "immunocompromised_status",
    type: QuestionType.RADIO,
    question: "Are you immunocompromised or taking immunosuppressive medications?",
    helpText: "This includes conditions like HIV, cancer treatment, organ transplant, or medications like prednisone, methotrexate, or rituximab.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am immunocompromised or take immunosuppressive medications",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not immunocompromised and do not take immunosuppressive medications"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants who are immunocompromised or taking immunosuppressive medications.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
      ],
      no: [
        { attributeDefinitionId: 3052, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "heart_inflammation",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation) in the past 3 months?",
    helpText: "These are rare inflammatory conditions affecting the heart.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with myocarditis or pericarditis in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with myocarditis or pericarditis in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with recent myocarditis or pericarditis cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25888, booleanAttributeValue: true }, // history_myocarditis_pericarditis_prerequisite
      ],
      no: [
        { attributeDefinitionId: 25888, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "guillain_barre",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with Guillain-Barré syndrome?",
    helpText: "Guillain-Barré syndrome is a rare disorder where the body's immune system attacks the nerves.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with Guillain-Barré syndrome",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with Guillain-Barré syndrome"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of Guillain-Barré syndrome cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25742, booleanAttributeValue: true }, // had_guillianbarre_prerequisite
      ],
      no: [
        { attributeDefinitionId: 25742, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "pregnancy_breastfeeding",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently pregnant or breastfeeding"
      },
      {
        value: "not_applicable",
        label: "Not applicable (I am male or cannot become pregnant)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who are pregnant or breastfeeding cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26453, booleanAttributeValue: true }, // is_pregnant_prerequisite
        { attributeDefinitionId: 26455, booleanAttributeValue: true }, // is_breastfeeding_prerequisite
      ],
      no: [
        { attributeDefinitionId: 26453, booleanAttributeValue: false },
        { attributeDefinitionId: 26455, booleanAttributeValue: false },
      ],
      not_applicable: [],
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
